/**
 * The undo history stack (W1-09 state-store; ARCHITECTURE.md §5.3 and §10, DESIGN.md §5.13).
 *
 * One entry per user action. An entry is identified by the seq of the transaction that pushed it; coalesced
 * transactions merge into it (ops composed, label / time / selection-after of the latest). `position` is the index of
 * the last applied entry (-1 = bottom); entries above it are redo-able until the next push truncates them.
 * Memory: every entry carries an estimate of its ops and selections (`itemBytes`); above the cap (256 MB by
 * default) the oldest entries are dropped (one entry is always kept) and the drop is reported.
 *
 * Persisted undo is DERIVED from the journal: every committed Txn carries a HistMarker, and `foldJournal` replays
 * those markers through this same class, so the folded history is the live one (same entries, ops, position and
 * drops). The folded form of an entry is a Txn (`hist: push`, `seq` = the entry's seq, `ops` = composed ops), which
 * is what `DocStore.load` and `OpenedProject.history` carry. A marker that does not fit the folded stack (a merge,
 * undo or redo of an entry outside the window, or a stale reference) cannot be trusted: the fold clears the stack
 * at that point and reports `truncated` (later entries are still exact, because they apply on top of that state).
 * A `reset` marker inside the window (Clear history, or a history the store found stale) makes the whole fold
 * untrusted (ARCHITECTURE.md §5.3 and §10, `OpenedProject.history`): the result is EMPTY with `truncated`, so the
 * project reopens with "Earlier changes: see Versions" -- the same rule as the persist worker's fold.
 *
 * Entries without net effect (a coalesced run that came back to where it started, e.g. nudge +10 then -10) stay
 * in the stack, so the journal markers and every fold agree; `entries()` flags them (`noEffect`) and the DocStore
 * undoes and redoes them together with the step they follow, so Ctrl+Z always changes something visible.
 *
 * Pure: imports core only (the persist worker cannot import state/; change request CR-W1-09-1 proposes moving the
 * fold to core so persist can share it verbatim).
 */

import type { Unsubscribe } from '../core/util/types';
import { Ops } from '../core/ops/ops';
import type { HistMarker, Op, SelectionSnapshot, Txn, TxnFocus, TxnKind } from '../core/ops/types';
import type { MessageRef } from '../core/util/types';
import type { CoalesceTarget } from './coalesce';
import { Emitter } from './emitter';
import type { HistoryEntry, HistoryView } from './types';

/** Default undo memory cap: 256 MB. */
export const DEFAULT_UNDO_CAP_BYTES = 256 * 1024 * 1024;

// ---------------------------------------------------------------------------------------------------------------
// Memory estimate
// ---------------------------------------------------------------------------------------------------------------

const OP_OVERHEAD = 64;
const MAX_DEPTH = 24;

/** Rough retained size of a plain value (numbers 8, strings 2 per char, objects and arrays with slot overhead). */
export function estimateValueBytes(v: unknown, depth = 0): number {
  switch (typeof v) {
    case 'number':
      return 8;
    case 'boolean':
      return 4;
    case 'string':
      return 16 + 2 * v.length;
    case 'bigint':
      return 16;
    case 'object': {
      if (v === null) return 0;
      if (depth >= MAX_DEPTH) return 64;
      if (Array.isArray(v)) {
        let n = 16 + 8 * v.length;
        for (const x of v as readonly unknown[])
          if (typeof x === 'object' || typeof x === 'string') n += estimateValueBytes(x, depth + 1);
        return n;
      }
      let n = 32;
      for (const key of Object.keys(v)) {
        n += 16;
        n += estimateValueBytes((v as Readonly<Record<string, unknown>>)[key], depth + 1);
      }
      return n;
    }
    default:
      return 0;
  }
}

/** Estimated bytes kept alive by an op list (deleted and replaced records count in full). */
export function estimateOpsBytes(ops: readonly Op[]): number {
  let n = 16 + 8 * ops.length;
  for (const op of ops) {
    n += OP_OVERHEAD;
    switch (op.o) {
      case 'ins':
      case 'del':
        n += estimateValueBytes(op.rec);
        break;
      case 'set':
      case 'single':
        n += estimateValueBytes(op.prev) + estimateValueBytes(op.next);
        break;
      case 'order':
        n += 16 + 8 * (op.prev.length + op.next.length);
        break;
      case 'idSeq':
        break;
    }
  }
  return n;
}

function estimateSelectionBytes(s: SelectionSnapshot): number {
  return (
    96 +
    8 *
      (s.notes.length +
        s.keys.length +
        s.segments.length +
        s.lines.length +
        s.bpm.length +
        s.markers.length +
        s.effects.length +
        s.videos.length +
        s.groups.length)
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------------------------------------------

/**
 * Invariant violations (opaque keys) that the documents before and after an entry already had, recorded by a
 * strict DocStore when the entry is committed. Undo restores the state before the entry and redo the state after
 * it, so those violations are not new when a replay brings them back. Null when unknown (non-strict store, or an
 * entry restored from the journal).
 */
export interface ReplayAllowance {
  readonly undo: ReadonlySet<string>;
  readonly redo: ReadonlySet<string>;
}

/** True when `ops` change a record, singleton or order list (an idSeq op alone changes nothing visible). */
export function hasRecordOps(ops: readonly Op[]): boolean {
  for (const op of ops) if (op.o !== 'idSeq') return true;
  return false;
}

/** One history entry (internal, mutable while it is the top of a coalescing run). */
export interface HistoryItem extends CoalesceTarget {
  /** Seq of the transaction that pushed the entry (its identity in HistMarkers). */
  readonly seq: number;
  readonly kind: TxnKind;
  label: MessageRef;
  command: string | undefined;
  /** Forward ops of the entry (composed when transactions were merged). */
  ops: readonly Op[];
  readonly selBefore: SelectionSnapshot;
  selAfter: SelectionSnapshot;
  focus: TxnFocus | undefined;
  /** Epoch ms of the latest transaction of the entry. */
  at: number;
  coalesceKey: string | undefined;
  lastSeq: number;
  lastAt: number;
  sealed: boolean;
  bytes: number;
  /** What a replay of the entry may restore without being refused (see `ReplayAllowance`). */
  allow: ReplayAllowance | null;
}

function itemBytes(ops: readonly Op[], selBefore: SelectionSnapshot, selAfter: SelectionSnapshot): number {
  return 128 + estimateOpsBytes(ops) + estimateSelectionBytes(selBefore) + estimateSelectionBytes(selAfter);
}

/** A new entry from the transaction that pushes it. */
export function itemFromTxn(txn: Txn, lastSeq: number = txn.seq, allow: ReplayAllowance | null = null): HistoryItem {
  return {
    seq: txn.seq,
    kind: txn.kind,
    label: txn.label,
    command: txn.command,
    ops: txn.ops,
    selBefore: txn.selBefore,
    selAfter: txn.selAfter,
    focus: txn.focus,
    at: txn.at,
    coalesceKey: txn.coalesceKey,
    lastSeq,
    lastAt: txn.at,
    sealed: false,
    bytes: itemBytes(txn.ops, txn.selBefore, txn.selAfter),
    allow,
  };
}

/** The folded (persisted) form of an entry: a Txn with `hist: push`. */
export function itemToTxn(item: HistoryItem): Txn {
  const base = {
    seq: item.seq,
    kind: item.kind,
    label: item.label,
    ops: item.ops,
    at: item.at,
    selBefore: item.selBefore,
    selAfter: item.selAfter,
    hist: { k: 'push' } as HistMarker,
  };
  return {
    ...base,
    ...(item.coalesceKey !== undefined ? { coalesceKey: item.coalesceKey } : {}),
    ...(item.focus !== undefined ? { focus: item.focus } : {}),
    ...(item.command !== undefined ? { command: item.command } : {}),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// The stack
// ---------------------------------------------------------------------------------------------------------------

/** The history view of the contract (version, dropped entries, truncated flag and folded form, CR-W1-09-2). */
export class UndoHistory implements HistoryView {
  private items: HistoryItem[] = [];
  private pos = -1;
  private bytes = 0;
  private ver = 0;
  private dropped = 0;
  private trunc = false;
  private cached: { readonly version: number; readonly entries: readonly HistoryEntry[] } | null = null;
  private readonly listeners = new Emitter<null>();
  private readonly capBytes: number;

  constructor(capBytes: number = DEFAULT_UNDO_CAP_BYTES) {
    this.capBytes = Number.isFinite(capBytes) && capBytes > 0 ? capBytes : DEFAULT_UNDO_CAP_BYTES;
  }

  get length(): number {
    return this.items.length;
  }
  get position(): number {
    return this.pos;
  }
  get canUndo(): boolean {
    return this.pos >= 0;
  }
  get canRedo(): boolean {
    return this.pos < this.items.length - 1;
  }
  get memoryBytes(): number {
    return this.bytes;
  }
  get version(): number {
    return this.ver;
  }
  get truncated(): boolean {
    return this.trunc;
  }
  get droppedEntries(): number {
    return this.dropped;
  }
  /** The memory cap in bytes. */
  get cap(): number {
    return this.capBytes;
  }

  /** The entry at the current position (the next undo), if any. */
  current(): HistoryItem | undefined {
    return this.pos >= 0 ? this.items[this.pos] : undefined;
  }

  /** The entry above the current position (the next redo), if any. */
  next(): HistoryItem | undefined {
    return this.items[this.pos + 1];
  }

  /** True when the current position is the newest entry (no redo tail). */
  atTop(): boolean {
    return this.pos === this.items.length - 1;
  }

  item(index: number): HistoryItem | undefined {
    return this.items[index];
  }

  /** Index of the entry pushed at `seq`, or -1. */
  indexOfSeq(seq: number): number {
    for (let i = this.items.length - 1; i >= 0; i--) if (this.items[i]?.seq === seq) return i;
    return -1;
  }

  /**
   * Appends an entry for `txn` above the current position (the redo tail is discarded). `allow`: violations the
   * states before / after the entry had (strict stores). Returns entries dropped by the cap.
   */
  push(txn: Txn, allow: ReplayAllowance | null = null): number {
    if (this.pos < this.items.length - 1) {
      for (let i = this.pos + 1; i < this.items.length; i++) this.bytes -= this.items[i]?.bytes ?? 0;
      this.items.length = this.pos + 1;
    }
    const item = itemFromTxn(txn, txn.seq, allow);
    this.items.push(item);
    this.bytes += item.bytes;
    this.pos = this.items.length - 1;
    const dropped = this.enforceCap();
    this.changed();
    return dropped;
  }

  /**
   * Merges `txn` into the entry at the current position (which must be the top). Ops are composed; label, time,
   * focus, command and selection-after become those of `txn`. `allow`: violations the states before / after `txn`
   * had (the entry's undo keeps the allowance of its first transaction, its redo takes the latest). The merged
   * entry may end up without net effect (`noEffect`); it stays, so the journal markers keep folding to it.
   * Returns entries dropped by the cap.
   */
  merge(txn: Txn, allow: ReplayAllowance | null = null): number {
    const top = this.current();
    if (top === undefined || !this.atTop()) throw new Error('history.merge: no top entry to merge into');
    top.allow = top.allow !== null && allow !== null ? { undo: top.allow.undo, redo: allow.redo } : null;
    top.ops = Ops.compose([...top.ops, ...txn.ops]);
    top.label = txn.label;
    top.at = txn.at;
    top.selAfter = txn.selAfter;
    if (txn.focus !== undefined) top.focus = txn.focus;
    if (txn.command !== undefined) top.command = txn.command;
    top.lastSeq = txn.seq;
    top.lastAt = txn.at;
    this.bytes -= top.bytes;
    top.bytes = itemBytes(top.ops, top.selBefore, top.selAfter);
    this.bytes += top.bytes;
    const dropped = this.enforceCap();
    this.changed();
    return dropped;
  }

  /** Marks the current top entry so the next transaction does not merge into it. */
  seal(): void {
    const top = this.current();
    if (top === undefined || top.sealed) return;
    top.sealed = true;
  }

  /** Records that the entry at the current position was undone. */
  stepBack(): void {
    if (this.pos < 0) throw new Error('history.stepBack: nothing to undo');
    this.pos--;
    this.changed();
  }

  /** Records that the entry above the current position was redone. */
  stepForward(): void {
    if (this.pos >= this.items.length - 1) throw new Error('history.stepForward: nothing to redo');
    this.pos++;
    this.changed();
  }

  /** Clears the history. `truncated` tells whether earlier changes existed that can no longer be undone. */
  reset(truncated = false): void {
    this.items = [];
    this.pos = -1;
    this.bytes = 0;
    this.dropped = 0;
    this.trunc = truncated;
    this.changed();
  }

  /**
   * Replaces the history with folded entries (Txns with `hist: push`) and a position. A list that contains other
   * markers is a raw journal window and is folded first (its own position wins).
   */
  load(history: readonly Txn[], position?: number, truncated = false): void {
    if (history.some((t) => t.hist.k !== 'push')) {
      const folded = foldJournal(history, { capBytes: this.capBytes });
      this.loadItems(folded.history, folded.position, truncated || folded.truncated);
      return;
    }
    this.loadItems(history, position ?? history.length - 1, truncated);
  }

  private loadItems(history: readonly Txn[], position: number, truncated: boolean): void {
    this.items = history.map((t) => itemFromTxn(t, -1));
    this.bytes = this.items.reduce((n, it) => n + it.bytes, 0);
    const p = Number.isInteger(position) ? position : this.items.length - 1;
    this.pos = Math.max(-1, Math.min(this.items.length - 1, p));
    this.dropped = 0;
    this.trunc = truncated;
    this.enforceCap();
    this.changed();
  }

  /** The folded form (entries as push Txns, oldest first). */
  // Bound (arrow) members: consumers may pass them around (`const { subscribe } = store.history`).
  readonly toTxns = (): readonly Txn[] => this.items.map(itemToTxn);

  readonly entries = (): readonly HistoryEntry[] => {
    if (this.cached?.version === this.ver) return this.cached.entries;
    const entries = this.items.map((it, i): HistoryEntry => {
      const base = { seq: it.seq, label: it.label, at: it.at, undone: i > this.pos };
      return {
        ...base,
        ...(it.command !== undefined ? { command: it.command } : {}),
        ...(hasRecordOps(it.ops) ? {} : { noEffect: true as const }),
      };
    });
    this.cached = { version: this.ver, entries };
    return entries;
  };

  readonly subscribe = (cb: () => void): Unsubscribe =>
    this.listeners.subscribe(() => {
      cb();
    });

  /**
   * Drops entries while above the cap, always keeping one. Applied entries go first, oldest first (the state they
   * lead to stays the base of everything above them); only when no applied entry is left (a history loaded at a low
   * position) are redo-able entries dropped, newest first, so the remaining redo steps still apply in order. In the
   * live store and the journal fold the position is always the top here (push and merge discard the redo tail), so
   * only the oldest entries are ever dropped there.
   */
  private enforceCap(): number {
    let older = 0;
    while (this.bytes > this.capBytes && this.items.length > 1 && this.pos >= 0) {
      const gone = this.items.shift();
      if (gone === undefined) break;
      this.bytes -= gone.bytes;
      this.pos--;
      older++;
    }
    let newer = 0;
    while (this.bytes > this.capBytes && this.items.length > 1 && this.pos < this.items.length - 1) {
      const gone = this.items.pop();
      if (gone === undefined) break;
      this.bytes -= gone.bytes;
      newer++;
    }
    if (older > 0) this.trunc = true;
    this.dropped += older + newer;
    return older + newer;
  }

  private changed(): void {
    this.ver++;
    this.listeners.emit(null);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Journal fold
// ---------------------------------------------------------------------------------------------------------------

export interface FoldedHistory {
  /** Entries in their folded form (oldest first). */
  readonly history: readonly Txn[];
  /** Index of the last applied entry (-1 = bottom). */
  readonly position: number;
  /**
   * Earlier changes cannot be undone: a marker did not fit (window started mid-history, stale reference; history
   * before that point is lost), the memory cap dropped entries, or a `reset` lies inside the window (then the
   * history is empty).
   */
  readonly truncated: boolean;
  /** Entries dropped by the memory cap during the fold. */
  readonly dropped: number;
}

export interface FoldOptions {
  readonly capBytes?: number;
}

/** The fold of a window that cannot be trusted: no history, "Earlier changes: see Versions". */
function untrustedFold(dropped: number): FoldedHistory {
  return { history: [], position: -1, truncated: true, dropped };
}

/**
 * Folds the HistMarkers of committed transactions (in seq order) into the undo history they describe:
 * push = new entry above the position (redo tail discarded); merge = coalesced into the top entry pushed at `into`;
 * undo / redo = the entry pushed at `of` was undone / redone. A marker that does not fit clears the stack at that
 * point (`truncated`). A `reset` anywhere in the window makes the fold untrusted (ARCHITECTURE.md §5.3, §10): the
 * result is empty with `truncated`, whatever follows it.
 */
export function foldJournal(journal: readonly Txn[], opts: FoldOptions = {}): FoldedHistory {
  const h = new UndoHistory(opts.capBytes);
  let dropped = 0;
  const lost = (): void => {
    h.reset(true);
  };
  for (const txn of journal) {
    const m = txn.hist;
    switch (m.k) {
      case 'push':
        dropped += h.push(txn);
        break;
      case 'merge': {
        const top = h.current();
        if (top?.seq === m.into && h.atTop()) dropped += h.merge(txn);
        else lost();
        break;
      }
      case 'undo':
        if (h.current()?.seq === m.of) h.stepBack();
        else lost();
        break;
      case 'redo':
        if (h.next()?.seq === m.of) h.stepForward();
        else lost();
        break;
      case 'reset':
        return untrustedFold(dropped);
    }
  }
  return { history: h.toTxns(), position: h.position, truncated: h.truncated, dropped };
}
